import { SaveOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button, Card, Col, message, Row,
  Space, Spin, Typography
} from 'antd';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DropdownElement } from 'common/components/DropdownType';
import HeaderPage from 'common/components/HeaderPage';
import Input from 'common/components/Input';
import { createBankService, getBankByIdService, updateBankService } from 'common/services/extends/banks';
import { CreateBankParams } from 'common/services/extends/banks/types';
import { ROUTE_PATHS } from 'common/utils/constant';
import { bankSchema } from 'common/utils/schemas';

interface BankForm {
  name: string;
  // code: string;
  branch: string;
  region: {
    label: string;
    value: string;
  };
}

const defaultValues = {
  name: '',
  // code: '',
  branch: '',
  region: {
    label: '',
    value: '',
  }
};

const BankDetail: React.FC<ActiveRoles> = ({ roleIndex, roleUpdate }) => {
  /* Hooks */
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const id = Number(searchParams.get('id'));
  /* States */

  /* React-hook-form */
  const method = useForm<BankForm>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(bankSchema),
  });

  const queryKey = ['bank-detail', id];

  const { isLoading } = useQuery(
    queryKey,
    () => getBankByIdService(id),
    {
      enabled: !!id && roleIndex,
      onSuccess: (data) => {
        method.reset({
          name: data.bankData?.name || '',
          // code: data.bankData?.code,
          branch: data.bankData?.branchName,
          region: {
            label: data.province?.name,
            value: data.province?.code,
          }
        });
      },
    }
  );

  const { mutate: createBankMutate, isLoading: createLoading } = useMutation(
    ['create-bank'],
    async (params: CreateBankParams) => createBankService(params),
    {
      onSuccess: () => {
        message.success(t('message.createSuccess'));
        navigate(ROUTE_PATHS.BANK_MANAGEMENT);
      },
      onError: () => {
        message.error(t('message.createError'));
      }
    }
  );

  const { mutate: updateBankMutate, isLoading: updateLoading } = useMutation(
    ['update-bank'],
    async (params: CreateBankParams & { id: number }) => updateBankService({ ...params, id }),
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
        queryClient.invalidateQueries(queryKey);
      },
      onError: () => {
        message.error(t('message.updateError'));
      }
    }
  );

  /* Functions */
  const onSubmit = async () => {
    const isValid = await method.trigger();
    if (!isValid) return;

    const formData = method.getValues();

    const submitData = {
      name: formData.name,
      // code: formData.code,
      branchName: formData.branch,
      provinceCode: formData.region.value,
    };

    if (id) {
      updateBankMutate({
        id,
        ...submitData
      });
    } else {
      createBankMutate(submitData);
    }
  };

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.bank')}
        rightHeader={(
          <Space size={16}>
            <Button
              type="primary"
              disabled={!!id && !roleUpdate}
              onClick={method.handleSubmit(onSubmit)}
            >
              <SaveOutlined />
              {t('system.save')}
            </Button>
          </Space>
        )}
      />
      <Spin size="large" spinning={isLoading || createLoading || updateLoading}>
        <div className="t-mainlayout_wrapper">
          <FormProvider {...method}>
            <Row gutter={16}>
              <Col xxl={24}>
                <Card type="inner">
                  <div className="site-card-border-less-wrapper">
                    <Space
                      direction="vertical"
                      size={12}
                      style={{ width: '100%' }}
                    >
                      <Row gutter={16}>
                        <Col span={12} className="u-mt-8">
                          <Typography.Text strong>
                            {t('system.name')}
                            {' '}
                          </Typography.Text>
                          <Typography.Text strong type="danger">
                            *
                          </Typography.Text>
                          <Controller
                            name="name"
                            control={method.control}
                            render={({
                              field: { value, onChange },
                              fieldState: { error },
                            }) => (
                              <Input
                                className="u-mt-8"
                                value={value}
                                onChange={onChange}
                                error={error?.message}
                                size="large"
                              />
                            )}
                          />
                        </Col>
                        {/* <Col span={12} className="u-mt-8">
                          <Typography.Text strong>
                            {t('system.code')}
                            {' '}
                          </Typography.Text>
                          <Typography.Text strong type="danger">
                            *
                          </Typography.Text>
                          <Controller
                            name="code"
                            control={method.control}
                            render={({
                              field: { value, onChange },
                              fieldState: { error },
                            }) => (
                              <Input
                                className="u-mt-8"
                                value={value}
                                onChange={onChange}
                                error={error?.message}
                                size="large"
                                disabled={!!id}
                              />
                            )}
                          />
                        </Col> */}
                        <Col span={12} className="u-mt-8">
                          <Typography.Text strong>
                            {t('bank.region')}
                            {' '}
                          </Typography.Text>
                          <Typography.Text strong type="danger">
                            *
                          </Typography.Text>
                          <Controller
                            name="region"
                            control={method.control}
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                              <>
                                <DropdownElement
                                  type="province"
                                  locale="vi"
                                  isGetOption
                                  value={value}
                                  isShowSearch
                                  isValueSlug
                                  onChange={onChange}
                                  size="large"
                                />
                                {error && (
                                  <span
                                    className="a-input_errorMessage"
                                  >
                                    {(error as any)?.value?.message}
                                  </span>
                                )}
                              </>
                            )}
                          />
                        </Col>
                        <Col span={24} className="u-mt-8">
                          <Typography.Text strong>
                            {t('bank.branch')}
                            {' '}
                          </Typography.Text>
                          <Typography.Text strong type="danger">
                            *
                          </Typography.Text>
                          <Controller
                            name="branch"
                            control={method.control}
                            render={({
                              field: { value, onChange },
                              fieldState: { error },
                            }) => (
                              <Input
                                className="u-mt-8"
                                value={value}
                                onChange={onChange}
                                error={error?.message}
                                size="large"
                              />
                            )}
                          />
                        </Col>
                      </Row>
                    </Space>
                  </div>
                </Card>
              </Col>
            </Row>
          </FormProvider>
        </div>
      </Spin>
    </>
  );
};

export default BankDetail;
